import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { useNavigate, useParams } from "react-router";
import { useLocation } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Paper from "@mui/material/Paper";

import TableEntities from "../../components/TableEntities/index";
import { FAVORITE_REPORTS_QUERY, NOTIFICATIONS_QUERY } from "../../constants/graphql";
import BillingReportViewerModal from "../../components/project/billing/BillingReportViewerModal";
import GenerateReportModal from "./GenerateReportModal";
import ReportsTable from "./ReportsTable";
import SelectProjectModal from "./SelectProjectModal";
import FavouriteReportActions from "./FavouriteReportActions";
import ReportConfiguration from "./ReportConfiguration";
import { usePermissions } from "../../contexts/permissions";
import GenerateReport from "./GenerateReport";

const styleLabels = {
  landscape: "Landscape",
  portrait: "Portrait",
};

const pageSizeLabels = {
  "8.5x11": `Letter (8.5"x11")`,
  "11x17": `Tabloid (11"x17")`,
};

export const adminConfiguration = {
  key: "templates",
  title: "",
  fields: [
    {
      label: "Report Template Name",
      path: "payload.name",
    },
  ],
  nestedTable: {
    key: "reports",
    title: "Report History",
    arrowTooltip: "Expand History",
    component: ReportsTable,
  },
  customLink: FavouriteReportActions,
};

export const configuration = {
  key: "templates",
  title: "",
  fields: [
    {
      label: "Report Template Name",
      path: "payload.name",
    },
  ],
  customLink: GenerateReport,
};

const reportClientId = "reportClientId";
const reportProjectId = "reportProjectId";
const isBillingReport = title =>
  title?.includes("Billing Report (Prototype)") ||
  title?.includes("Billing Report (Validation)") ||
  title?.includes("Multi-project billing report");

export default function PageContent() {
  const { user } = usePermissions();
  const defaultClientId = localStorage.getItem(reportClientId);
  const defaultProjectId = localStorage.getItem(reportProjectId);
  const [selectedClientId, setSelectedClientId] = useState(defaultClientId);
  const [selectedProjectId, setSelectedProjectId] = useState(defaultProjectId);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const isAdminOrClient = ["admin", "client"].includes(user.role);
  const { id, notificationId } = useParams();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const configureReportId = searchParams.get("configure");
  const viewerOwnerId = searchParams.get("ownerId");
  const { data: { favoriteReports = [] } = {}, loading, refetch } = useQuery(
    FAVORITE_REPORTS_QUERY
  );
  const { data: { notifications: viewerNotifications = [] } = {}, loading: viewerLoading } = useQuery(
    NOTIFICATIONS_QUERY,
    {
      variables: { type: "report", ownerId: viewerOwnerId || undefined },
      skip: !notificationId,
    }
  );

  const reports = favoriteReports.map(v => ({
    ...v,
    styleLabel: styleLabels[v.payload.style],
    pageSizeLabel: pageSizeLabels[v.payload.page_size],
  }));
  const selectedBillingNotification = useMemo(() => {
    if (!notificationId) return null;

    return (
      viewerNotifications.find(
        notification => notification.id === notificationId && isBillingReport(notification.title)
      ) || null
    );
  }, [notificationId, viewerNotifications]);

  const filteredReports = selectedClientId
    ? reports.filter(report =>
        report.notifications.some(notification =>
          notification?.client?.id === selectedClientId && selectedProjectId === "all"
            ? true
            : notification?.project?.id === selectedProjectId
        )
      )
    : reports;

  const list = filteredReports.filter(report =>
    (report?.payload?.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [reportModal, setReportModal] = useState(false);
  const [selectedReport] = useState(null);

  const toggleReportModal = () => setReportModal(v => !v);

  const newReportTemplate = ({ projectId }) => {
    navigate(`/projects/${projectId}/templates/new`);
  };
  const handleCloseBillingViewer = () => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("ownerId");
    navigate({
      pathname: "/insights",
      search: nextParams.toString(),
    });
  };

  return (
    <Box position="relative">
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontSize={22} fontWeight={500} color="text.secondary">
            Report Templates
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate("/projects/0/templates/new")}
          >
            New Template
          </Button>
        </Box>
        <Box mt={2} display="flex">
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search"
            value={searchTerm}
            size="small"
            sx={{
              maxWidth: 400,
              backgroundColor: "white",
            }}
            onChange={e => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>
      <TableEntities
        loading={loading}
        entities={list}
        disablePagination={true}
        setEntities={refetch}
        configuration={isAdminOrClient ? adminConfiguration : configuration}
        sort={{ order: "payload.name" }}
        scrollable={false}
      />
      {reportModal && selectedReport && (
        <GenerateReportModal
          projectId={selectedReport.projectId}
          favoriteReportId={selectedReport.id}
          title={selectedReport.name}
          onSubmit={toggleReportModal}
          onClose={toggleReportModal}
        />
      )}
      {id && id === "new" && (
        <SelectProjectModal
          title="New Report Template"
          buttonLabel="Next"
          onSubmit={newReportTemplate}
          onClose={() => navigate("/insights")}
        />
      )}
      {configureReportId && (() => {
        const configEntity = reports.find(r => r.id === configureReportId);
        if (!configEntity) return null;
        return (
          <ReportConfiguration
            open
            onClose={() => navigate("/insights")}
            entity={configEntity}
            setEntities={refetch}
          />
        );
      })()}
      {notificationId && selectedBillingNotification && !viewerLoading && (
        <BillingReportViewerModal
          open
          onClose={handleCloseBillingViewer}
          fileUrl={selectedBillingNotification.link}
          fileName={selectedBillingNotification.title}
        />
      )}
    </Box>
  );
}
